import { useCollection } from './siteData'
import type { ServicePageRow, OfferRow } from '@/schemas'
import { isServicePagePath } from '@/content/servicePages'

/**
 * Is the thing this href points at actually live right now?
 *
 * Phase 2 seeds four specialist pages and five offers unpublished, on the
 * owner's instruction that new content lands hidden until approved. That only
 * works if "hidden" is coherent across every surface — otherwise the
 * navigation advertises a page that 404s, the homepage problem grid links into
 * nothing, and the contact form offers a service it cannot resolve.
 *
 * One predicate, four consumers: the nav's Fractional CTO item, the problem
 * grid's tiles, the /workshops CTA, and the contact form's service list.
 * Publishing a row in /admin flips all of them at once, with no redeploy.
 *
 * The check is on the FLAG, not on the row being present. An admin session
 * receives unpublished rows from the same endpoints (that is what makes draft
 * preview work), so presence-based gating would show the owner links that
 * nobody else can follow — the opposite of a useful preview.
 */
export interface LiveTarget {
  /** True once the target is published/visible. False while loading. */
  isLive: (href: string) => boolean
  /** Still fetching. Callers hide gated links rather than flash a dead one. */
  isLoading: boolean
}

/** `/contact?offer=<slug>` → `<slug>`, else null. */
function offerSlugFromHref(href: string): string | null {
  const q = href.indexOf('?')
  if (q === -1) return null
  const params = new URLSearchParams(href.slice(q + 1))
  return params.get('offer')
}

export function useLiveTarget(): LiveTarget {
  const pages = useCollection<ServicePageRow>('service-pages')
  const offers = useCollection<OfferRow>('offers')

  const isLoading = pages.isLoading || offers.isLoading

  const isLive = (href: string): boolean => {
    if (!href) return false

    // A specialist page path is live only once its row says published.
    const path = href.split(/[?#]/)[0]
    if (isServicePagePath(path)) {
      return pages.data.some((p) => `/${p.slug}` === path && p.published === true)
    }

    // A /contact?offer=<slug> link always RESOLVES — /contact is never hidden —
    // but it can only prefill the service-interest select when the offer is
    // visible. Callers that care about prefill (the workshops CTA) gate on
    // this; callers that only care about attribution do not, because Phase 1.1
    // records cta_id from the raw query string either way.
    const offerSlug = offerSlugFromHref(href)
    if (offerSlug) {
      return offers.data.some((o) => o.slug === offerSlug && o.visible === true)
    }

    // Anything else — /services, /portfolio, an anchor, an external URL — is a
    // route that exists unconditionally.
    return true
  }

  return { isLive, isLoading }
}
